/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener } from "@/types";
import { getComponentConfig, getComponentElements } from "@/utils/dom/component";
import { merge } from "@/utils/merge";
import { Component } from "./component";
import { Modal, type ModalOptions } from "./modal";

export function initSearch() {
    getComponentElements(Search.selector).forEach(element => new Search(element));
}

export class Search extends Component<SearchEvents> {
    static selector = 'search';

    static defaults: Partial<SearchOptions> = {
        modalConfig: {
            modalClasses: ['search__modal'],
            initialFocusSelector: '[data-search="input"]'
        }
    };

    readonly selectors = {
        openButton: '[data-search="toggle"]',
        modalContent: '[data-search="dialog"]'
    };

    public element: HTMLElement;
    private openButton: HTMLElement;
    private modalContent: HTMLElement | null;

    public config: SearchOptions;

    private modal?: Modal;

    private clickListener: SpecificEventListener<'click'>;

    constructor(element: HTMLElement, options: Partial<SearchOptions> = {}) {
        super();

        this.element = element;
        this.config = merge({},
            Search.defaults,
            getComponentConfig(Search.selector, this.element),
            options
        ) as SearchOptions;

        this.clickListener = () => this.openModal();

        this.openButton = this.element.querySelector(this.selectors.openButton)!;
        this.modalContent = this.element.querySelector(this.selectors.modalContent);

        this.openButton.addEventListener('click', this.clickListener);

        if (this.modalContent) {
            this.modal = new Modal(this.modalContent, this.config.modalConfig);
        }
    }

    /**
     * @inheritdoc
     */
    public override destroy(): void {
        super.destroy();

        this.openButton.removeEventListener('click', this.clickListener);

        this.modal?.destroy();
        this.modal = null as any;

        this.element = null as any;
        this.openButton = null as any;
        this.modalContent = null as any;
    }

    /**
     * Open search modal
     */
    public openModal() {
        !this.modal?.isOpen && this.modal?.openModal();
    }

    /**
     * Close search modal
     */
    public closeModal() {
        this.modal?.isOpen && this.modal.closeModal();
    }
}

export interface SearchEvents {
}

export interface SearchOptions {
    modalConfig: Partial<ModalOptions>
}
